import { sectionViewFrontPlane } from "building-generation/generationTools/sectionViewClippingPlane";
import type { Building } from "building-types/building/buildingTypes";
import type { ElevationReferenceFrame } from "building-types/views/elevationReferenceFrame";
import type { View } from "building-types/views/GeneratedPageView";
import type { DocFaceDirection } from "building-types/walls2/wallPlaneFilters";
import { assertUnreachable } from "common/utils/assertUnreachable";
import type { PlaneBSP } from "geom/bspTypes";
import { planeNewXY, planeNewXZ, planeNewXZNeg, planeNewYZ, planeNewYZNeg, planeNormal } from "geom/plane";
import { planeBSPFlip, planeBSPFromPlane } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import { vec3Copy, vec3Dot, vec3Scale, type Vec3 } from "geom/vec3";
import { buildingGraphPlaneForHash, wallPlaneOutlineCentroidWorld } from "./wallPlaneBuilding";

const ELEV_ORIGIN_OFFSET = 5000;

export const elevationReferenceFramesToPlanes: {
  [key in ElevationReferenceFrame]: Plane;
} = {
  FRONT: planeNewXZ(0, 0, 0),
  BACK: planeNewXZNeg(0, 0, 0),
  RIGHT: planeNewYZ(0, 0, 0),
  LEFT: planeNewYZNeg(0, 0, 0),
} as const;

export const elevationReferenceFrameToViewDirection: {
  [key in ElevationReferenceFrame]: Vec3;
} = {
  FRONT: [0, 1, 0],
  BACK: [0, -1, 0],
  LEFT: [1, 0, 0],
  RIGHT: [-1, 0, 0],
};

export const elevationReferenceFrameToEyePosition: {
  [key in ElevationReferenceFrame]: Vec3;
} = {
  BACK: [0, ELEV_ORIGIN_OFFSET, 0],
  LEFT: [-ELEV_ORIGIN_OFFSET, 0, 0],
  RIGHT: [ELEV_ORIGIN_OFFSET, 0, 0],
  FRONT: [0, -ELEV_ORIGIN_OFFSET, 0],
};

export const elevationReferenceFrameToProjectionPlane: {
  [key in ElevationReferenceFrame]: Plane;
} = {
  FRONT: planeNewXZ(...elevationReferenceFrameToEyePosition["FRONT"]),
  BACK: planeNewXZNeg(...elevationReferenceFrameToEyePosition["BACK"]),
  RIGHT: planeNewYZ(...elevationReferenceFrameToEyePosition["RIGHT"]),
  LEFT: planeNewYZNeg(...elevationReferenceFrameToEyePosition["LEFT"]),
} as const;

export function wallPlaneOverrideOrthoViewSign(direction: DocFaceDirection): number {
  if (direction === "POSITIVE_NORMAL") {
    return -1;
  }
  return 1;
}

function scalePlaneNormalByOrthoViewSign(planeNormal: Vec3, sign: number): Vec3 {
  return vec3Copy(vec3Scale(planeNormal[0], planeNormal[1], planeNormal[2], sign));
}

/**
 * Ortho view direction for wall-plane override preview. The camera looks from the side
 * opposite the edited face so that face's finish is front-facing (e.g. pick +X normal side →
 * view along -X; pick -X side → view along +X).
 */
export function wallPlaneOverrideOrthoViewDirection(direction: DocFaceDirection, planeNormal: Vec3): Vec3 {
  return scalePlaneNormalByOrthoViewSign(planeNormal, wallPlaneOverrideOrthoViewSign(direction));
}

/**
 * Picking plane for wall-plane view mouse projection. THREE.js rejects ray–plane hits behind the
 * ray origin; flip the plane to face the ortho camera when it sits on the negative-normal side.
 */
export function wallPlanePickingPlaneBSP(plane: Plane, direction: DocFaceDirection): PlaneBSP {
  const bsp = planeBSPFromPlane(plane);
  const [normalX, normalY, normalZ] = planeNormal(plane);
  const [viewDirX, viewDirY, viewDirZ] = wallPlaneOverrideOrthoViewDirection(direction, [normalX, normalY, normalZ]);
  if (vec3Dot(viewDirX, viewDirY, viewDirZ, normalX, normalY, normalZ) > 0) {
    return planeBSPFlip(bsp);
  }
  return bsp;
}

/**
 * Ortho camera offset from the wall plane (world units); keeps the eye just inside the viewed space.
 * Wall plane view supports interior walls as well as exterior — `direction` selects which face of
 * the plane is being edited and drives camera/picking orientation.
 */
export const WALL_PLANE_OVERRIDE_ORTHO_CAMERA_DISTANCE = 36;

/** Near clip for wall-plane override ortho preview (camera is close to geometry). */
export const WALL_PLANE_OVERRIDE_ORTHO_CAMERA_NEAR = 4;

/**
 * Far clip for wall-plane override ortho preview, in world units
 * Keeps geometry behind the selected wall from bleeding into the view.
 * Adds current camera's distance (from the wall plane) with the offset value
 * to determine the placement of the far clipping plane. Steps that want this depth limiting
 * set `StepDefinition.wallPlaneCameraDepthLimit` to this value; other steps leave it undefined
 * so geometry behind the wall isn't clipped.
 */
const ORTHO_CAMERA_FAR_OFFSET = 14;
export const WALL_PLANE_OVERRIDE_ORTHO_CAMERA_FAR = WALL_PLANE_OVERRIDE_ORTHO_CAMERA_DISTANCE + ORTHO_CAMERA_FAR_OFFSET;

/** World-space look-at target on the wall plane outline centroid. */
export function wallPlaneOverrideCameraTarget(building: Building | undefined, planeHash: string): Vec3 | null {
  const centroid = wallPlaneOutlineCentroidWorld(building, planeHash);
  if (centroid) {
    return [centroid[0], centroid[1], centroid[2]];
  }
  const buildingPlane = building ? buildingGraphPlaneForHash(building, planeHash) : undefined;
  const plane = buildingPlane?.planeFace.plane;
  if (!plane) {
    return null;
  }
  // Keep the ortho camera on the actual wall plane when the outline loop is missing or too short;
  // exterior-focus fallback would place the eye on the wrong side and break POSITIVE_NORMAL picking.
  return [plane.originX, plane.originY, plane.originZ];
}

export function computeViewPlane(
  view: View,
  mirror: boolean,
  mirrorTranslationX: number,
  building?: Building,
): Plane | null {
  if (!view) {
    return null;
  }

  switch (view.entityType) {
    case "ELEVATION_VIEW":
      return elevationReferenceFramesToPlanes[view.referenceFrame];
    case "PLAN_VIEW":
      return planeNewXY();
    case "SECTION_VIEW":
      return sectionViewFrontPlane(view, mirror, mirrorTranslationX) ?? null;
    case "WALL_PLANE_VIEW": {
      const buildingPlane = building ? buildingGraphPlaneForHash(building, view.planeHash) : undefined;
      return buildingPlane?.planeFace.plane ?? null;
    }
    case "VIEW_3D":
    case "DATA_SEQUENCE_VIEW":
    case "GENERATED_PAGE_VIEW":
    case "CONTENT_TEMPLATE_VIEW":
    case "CONTENT_TEMPLATE_INSTANCE_VIEW":
      return null;
    default:
      return assertUnreachable(view);
  }
}
